import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { ManageProjectTeamsFormV2 } from '../Forms/ManageProjectTeamsFormV2'
import { useCurrentWorkspace } from '../useCurrentWorkspace'
import { useRestClient } from '@/contexts/QueryProvider'
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { isPlatformAdmin, isSuperUser } from '@/util/roles'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { Button } from '@/components/shadcn/button'
import { IconEditOutline } from '@icons/IconEditOutline'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { IconBg } from '@/components/ui/IconBg'
import { IconCommunityOutline } from '@icons/IconCommunityOutline'

export function ProjectTeamsSection() {
  const queryClient = useQueryClient()
  const [userRole] = useSessionStorage('roles', 1)
  const [isEditProjectTeamsAllowed] = useState(() => {
    return isPlatformAdmin(userRole) || isSuperUser(userRole)
  })
  const workspace = useCurrentWorkspace()
  const { workspaceId } = workspace
  const { getWorkspacesApiGetProjectTeamsWorkspace } = useRestClient()

  const {
    data: projectTeams,
    isPending: projectTeamsPending,
    isError: projectTeamsError,
  } = getWorkspacesApiGetProjectTeamsWorkspace.useQuery(
    {
      query: { workspaceId },
    },
    { enabled: !!workspaceId }
  )

  const onProjectTeamsSubmit = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workspaces', 'projectTeam', workspaceId] })
  }, [queryClient, workspaceId])

  return (
    <SectionContainer
      title="Project teams"
      description="Manage the project teams for the workspace."
      action={
        <ManageProjectTeamsFormV2
          sheetTitle="Manage Project Teams"
          sheetDescription="Manage the project teams for the workspace."
          sheetTriggerTooltip="Manage project teams"
          selectedWorkspace={workspace}
          onSubmit={onProjectTeamsSubmit}
          sheetTrigger={
            <Button disabled={!isEditProjectTeamsAllowed} variant="ghost" size="icon">
              <IconEditOutline />
            </Button>
          }
        />
      }
    >
      <div className="flex flex-row flex-wrap gap-2 whitespace-nowrap text-base">
        {projectTeamsPending ? (
          <LoadingComponent text="Loading project teams..." />
        ) : projectTeamsError ? (
          <ErrorComponent />
        ) : projectTeams.length === 0 ? (
          <span className="font-light italic">...No project teams found...</span>
        ) : (
          projectTeams.map((team, i) => (
            <div className="flex items-center gap-x-1" key={team.id}>
              <IconBg tooltip="Project team" className="size-8 bg-primary/30">
                <IconCommunityOutline className="size-4 text-muted-foreground" />
              </IconBg>
              <span key={team.id}>
                {team.name}
                {i < projectTeams.length - 1 ? ',' : ''}
              </span>
            </div>
          ))
        )}
      </div>
    </SectionContainer>
  )
}
